<script setup lang="ts">
import { computed } from 'vue';
import type { SalesMix } from '@/api';
import { mixSegments } from '@/lib/salesMix';

const props = defineProps<{ mix: SalesMix }>();
const segments = computed(() => mixSegments(props.mix));
const brandSegments = computed(() => segments.value.filter((segment) => segment.key !== 'unattributed'));
const unattributedSegment = computed(() => segments.value.find((segment) => segment.key === 'unattributed'));
const names = { suki: 'ลัคกี้สุกี้', bbq: 'ลัคกี้บาร์บีคิว', unattributed: 'ไม่ระบุ' } as const;
function fmtBaht(value: number) { return `฿${(value / 1_000_000).toFixed(1)}M`; }
</script>

<template>
  <section v-if="segments.length > 0" class="mix" aria-labelledby="sales-mix-title">
    <header class="mix__head">
      <div>
        <h3 id="sales-mix-title" class="mix__title">ส่วนแบ่งยอดขายตามแบรนด์</h3>
        <p class="mix__basis">เดือนนี้ · ยอดขายไม่รวม VAT</p>
      </div>
      <span class="mix__scope">รวมทุกแบรนด์</span>
    </header>

    <div class="mix__bar" aria-hidden="true">
      <span
        v-for="segment in segments"
        :key="segment.key"
        class="mix__segment"
        :class="`mix__segment--${segment.key}`"
        :style="{ width: `${segment.width}%` }"
      ></span>
    </div>

    <div class="mix__labels">
      <div
        v-for="segment in brandSegments"
        :key="`${segment.key}-label`"
        class="mix__brand"
        :class="`mix__brand--${segment.key}`"
      >
        <span class="mix__identity">
          <i :class="`mix__swatch--${segment.key}`" aria-hidden="true"></i>
          {{ names[segment.key] }}
        </span>
        <strong class="mix__percent">{{ segment.percent.toFixed(1) }}%</strong>
        <span class="mix__amount">{{ fmtBaht(segment.value) }}</span>
      </div>
    </div>

    <p class="mix__note">
      เปอร์เซ็นต์คิดจากยอดขายที่ระบุแบรนด์ได้
      <template v-if="unattributedSegment">
        · ไม่ระบุแบรนด์ {{ fmtBaht(unattributedSegment.value) }} ({{ unattributedSegment.percent.toFixed(1) }}% ของยอดขายรวม)
      </template>
    </p>
  </section>
</template>

<style scoped>
.mix {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.mix__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.mix__title {
  margin: 0;
  color: var(--text);
  font: var(--text-card-title);
}

.mix__basis {
  margin: 0.15rem 0 0;
  color: var(--text-dim);
  font: var(--text-caption);
}

.mix__scope {
  flex: 0 0 auto;
  padding: 0.2rem 0.45rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  color: var(--text-dim);
  font: var(--text-source);
  font-weight: 600;
}

.mix__bar {
  display: flex;
  height: 0.8rem;
  margin-top: 0.65rem;
  overflow: hidden;
  background: var(--surface-3);
  border-radius: var(--radius-bar);
}

.mix__segment {
  min-width: 4px;
  transition: width 0.24s ease;
}

.mix__segment--suki,
.mix__swatch--suki {
  background: var(--suki);
}

.mix__segment--bbq,
.mix__swatch--bbq {
  background: var(--bbq);
}

.mix__segment--unattributed {
  background: var(--text-dim);
}

.mix__labels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.55rem;
}

.mix__brand {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'identity percent'
    'amount percent';
  align-items: center;
  column-gap: 0.75rem;
  min-width: 0;
  min-height: 3.5rem;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--line);
  border-left-width: 3px;
  border-radius: var(--radius-chip);
}

.mix__brand--suki {
  background: var(--suki-soft);
  border-left-color: var(--suki);
}

.mix__brand--bbq {
  background: var(--bbq-soft);
  border-left-color: var(--bbq);
}

.mix__identity {
  grid-area: identity;
  min-width: 0;
  color: var(--text-dim);
  font: var(--text-card-label);
  font-weight: 600;
}

.mix__identity i {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.3rem;
  border-radius: var(--radius-round);
}

.mix__percent {
  grid-area: percent;
  color: var(--text);
  font: var(--text-kpi);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.mix__amount {
  grid-area: amount;
  margin-top: 0.08rem;
  color: var(--text);
  font: var(--text-card-title);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.mix__note {
  margin: 0.5rem 0 0;
  color: var(--text-dim);
  font: var(--text-source);
  line-height: 1.45;
}

@media (max-width: 640px) {
  .mix__labels {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 360px) {
  .mix__head {
    align-items: stretch;
    flex-direction: column;
    gap: 0.4rem;
  }

  .mix__scope {
    align-self: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mix__segment {
    transition: none;
  }
}
</style>
